import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import {
  WorkspaceChrome,
  WorkspaceSummary,
  WorkspaceActions,
  WorkspaceSection,
  WorkspaceStat,
  tabFromQuery,
} from '../../../platform/workspace360';
import { getBonus } from '../../../services/bonusService';
import BonusStatus from '../../../components/common/bonusStatus';

const TABS = [{ id: 'overview', label: 'Overview' }];

const STATUS_LABELS = {
  0: 'New',
  1: 'Paid',
  2: 'Completed',
  3: 'Canceled',
};

function capitalizeFirstLetter(string) {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
}

class Bonus360Panel extends Component {
  state = {
    bonus: null,
    loading: true,
    activeTab: 'overview',
  };

  componentDidMount() {
    this.syncTab();
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.load();
    }
    if (prevProps.location?.search !== this.props.location?.search) {
      this.syncTab();
    }
  }

  syncTab = () => {
    this.setState({
      activeTab: tabFromQuery(this.props.location?.search, 'overview', TABS),
    });
  };

  load = async () => {
    this.setState({ loading: true });
    try {
      const response = await getBonus(this.props.match.params.id);
      const bonus = response?.data?.data || response?.data;
      this.setState({ bonus, loading: false });
    } catch (ex) {
      this.setState({ loading: false });
      if (ex.response && ex.response.status === 404) {
        this.props.history.replace('/not-found');
      } else {
        toast.error('Failed to load bonus.');
      }
    }
  };

  handleTabChange = (tabId) => {
    this.setState({ activeTab: tabId });
  };

  bonusLabel(bonus) {
    if (!bonus.quantity || !bonus.unit) return 'N/A';
    const unit =
      bonus.quantity === 1
        ? capitalizeFirstLetter(String(bonus.unit).slice(0, -1))
        : capitalizeFirstLetter(String(bonus.unit));
    return `${bonus.quantity} ${unit}`;
  }

  renderOverview() {
    const { bonus } = this.state;
    return (
      <WorkspaceSection title="Overview" icon="fa-gift">
        <div className="customer360-grid-3">
          <WorkspaceStat label="Bonus ID" value={bonus.bonusid} isStatic />
          <WorkspaceStat
            label="Status"
            value={<BonusStatus status={bonus.status} />}
            isStatic
          />
          <WorkspaceStat label="Amount" value={this.bonusLabel(bonus)} isStatic />
          <WorkspaceStat label="Reason" value={bonus.reason} isStatic />
          <WorkspaceStat
            label="Account"
            value={
              bonus.account ? (
                <Link to={`/accounts/${bonus.account}`}>{bonus.account}</Link>
              ) : null
            }
            isStatic
          />
          <WorkspaceStat
            label="Date"
            value={bonus.createdAt ? moment(bonus.createdAt).format('Do MMM. YYYY') : null}
            isStatic
          />
        </div>
      </WorkspaceSection>
    );
  }

  render() {
    const { bonus, loading, activeTab } = this.state;
    if (loading) {
      return (
        <div className="customer360-view customer360-console py-3 customer360-muted">
          <i className="animate-spin fa fa-spinner" /> Loading bonus…
        </div>
      );
    }
    if (!bonus) return null;

    const summary = (
      <WorkspaceSummary
        title="Bonus Overview"
        icon="fa-gift"
        stats={[
          {
            key: 'status',
            label: 'Status',
            value: STATUS_LABELS[bonus.status] || '—',
            isStatic: true,
          },
          {
            key: 'amount',
            label: 'Amount',
            value: this.bonusLabel(bonus),
            isStatic: true,
          },
          {
            key: 'account',
            label: 'Account',
            value: bonus.account || '—',
            isStatic: true,
          },
        ]}
      />
    );

    const actions = (
      <WorkspaceActions>
        {bonus.account ? (
          <Link className="btn btn-light btn-sm" to={`/accounts/${bonus.account}`}>
            <i className="fa fa-server mr-1" aria-hidden /> Account
          </Link>
        ) : null}
      </WorkspaceActions>
    );

    return (
      <WorkspaceChrome
        title={bonus.bonusid || `Bonus #${bonus.id}`}
        subtitle={[STATUS_LABELS[bonus.status], this.bonusLabel(bonus)].filter(Boolean).join(' · ')}
        listLabel="Bonuses"
        listTo="/bonuses"
        recordLabel={bonus.bonusid || `Bonus #${bonus.id}`}
        summary={summary}
        actions={actions}
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={this.handleTabChange}
      >
        {this.renderOverview()}
      </WorkspaceChrome>
    );
  }
}

export default Bonus360Panel;
